import { Select, MenuItem, Stack, Typography, Button, Box, BoxProps } from "@mui/material";
import { Icons } from "../image/Icons";
import { getHexCode } from "../util/Color";
import { useEffect, useState } from "react";
import { PageContext } from "../pages/ConsolePages/PageContext";
import { useNavigate } from "react-router-dom";
import { LocalLoginUser } from "../schema/console/loginUser";
import { IDB } from "../api/IndexedDB/indexedDB";
import { GuestApi } from "../api/Api";
import useAsyncUpdate from "../util/useAsyncUpdate";
import { DeleteCookie, GetCookieValue, SetCookie } from "../util/CookieManager";
import Time from "../util/Time";
import { VoidAction } from "../util/func";

export function LoginSelect({ pageContext }: { pageContext: PageContext }) {
  const navigate = useNavigate()
  const [currentUsers, setCurrentUsers] = useState<LocalLoginUser[]>();
  const [, setOnline] = useState(pageContext.api.isOnline);

  useEffect(() => {
    const dispose = pageContext.api.subscribeOnlineState("LoginSelectUI", () => {
      setOnline(pageContext.api.isOnline);
    });
    return dispose;
  }, []);

  useEffect(() => {
    pageContext.api.login_user.getAll().then(setCurrentUsers);
  }, [pageContext]);

  const ArrowIcon = (props: BoxProps) => (
    <Box
      {...props}
      component="img"
      src={Icons.AccordionIcon}
      sx={{ width: 15, height: 10 }}
    />
  );

  return (
    <Select
      fullWidth
      value={""}
      onChange={async (e) => {
        const selectedValue = e.target.value.toString();
        if (selectedValue === "new") {
          navigate("/console/login/");
          return;
        }
        try {
          const userActiveState = await pageContext.api.user_data.getIsActive({ id: selectedValue });
          if (!userActiveState.isActive) {
            pageContext.api.showError(`ユーザーは既に削除済です。`);
            await pageContext.api.indexedDB.loginUserDataApi.Delete(selectedValue);
            pageContext.update();
          } else {
            await pageContext.api.SwitchUser(selectedValue);
            pageContext.update();
            navigate("/console/", { replace: true });  
          }
        } catch (e) {
          await pageContext.api.SwitchUser(selectedValue);
          pageContext.update();
          navigate("/console/");  
        }
      }}
      sx={{
        height: "48px",
        backgroundColor: getHexCode("White"),
        maxWidth: "200px",
        minWidth: "150px",
        "& .MuiSelect-icon": {
          top: "50%",
          transform: "translateY(-50%)",
        },
        "& .MuiSelect-iconOpen": {
          transform: "translateY(-50%) rotate(180deg)",
        },
      }}
      renderValue={() => 
        <Stack direction="row" sx={{ alignItems: "center", minWidth: "100px"}} gap={1}>
          <img src={pageContext.api.isOnline ?  Icons.GreenIcon_1 : Icons.GrayIcon}/>
          <Typography sx={{ overflow: "hidden", whiteSpace: "nowrap", textOverflow: "ellipsis", width: "150px" }}>{pageContext.api.currentUser.name}</Typography>
        </Stack>  
      }
      displayEmpty
      native={false}
      MenuProps={{
        sx: {
          '& .MuiMenuItem-root':{ width: "300px" }
        }
      }}
      IconComponent={ArrowIcon}
    >
      {currentUsers && currentUsers.map((user) => {
        return (
          <MenuItem key={user.id} value={user.id} sx={{ display: "flex", alignItems: "center", justifyContent: "center", py: 0 }}>
            <Stack direction="row" flex={1} alignItems="center" justifyContent="space-between">
              <Typography fontSize={16}>{user.name}</Typography>
              <Button onClick={async (e) => {
                e.stopPropagation();
                await pageContext.api.Logout(user.id);
                pageContext.api.login_user.getAll().then(setCurrentUsers);
              }}>
                <Typography fontSize={16} color={getHexCode("SubBlue")} sx={{ textDecoration: "underline" }}>ログアウト</Typography>
              </Button>
            </Stack>
          </MenuItem>
        )
      })}
      <MenuItem key="new" value={"new"} sx={{ height: "32px", display: "flex", alignItems: "center", justifyContent: "center" }}>
        <img width={12} height={12} src={Icons.AddIcon} />
      </MenuItem>
    </Select>
  );
}

export function LoginSelectForUnAutholizedPage({ updateApi }: { updateApi: VoidAction }) {
  const navigate = useNavigate();
  const [idb] = useAsyncUpdate(async () => { return new IDB(await IDB.initDB()) });
  const [currentUsers, updateCurrentUsers] = useAsyncUpdate(async () => { return idb?.loginUserDataApi.getAll() }, [idb]);
  const [currentUser, setCurrentUser] = useState<LocalLoginUser>();
  const [onlineState] = useAsyncUpdate(GuestApi.CheckIsOnline);

  useEffect(() => {
    const user = currentUsers?.find(user => user.token === GetCookieValue("app-auth-token"));
    setCurrentUser(user);
  }, [currentUsers]);

  if (!idb) {
    return null;
  }

  const ArrowIcon = (props: BoxProps) => (
    <Box
      {...props}
      component="img"
      src={Icons.AccordionIcon}
      sx={{ width: 15, height: 10 }}
    />
  );

  return (
    <Select
      fullWidth
      value={""}
      onChange={async (e) => {
        const selectedValue = e.target.value.toString();
        if (selectedValue === "new") {
          navigate("/console/login/");
          return;
        }
        try {
          const user = currentUsers?.find(user => user.id === selectedValue);
          if (user) {
            SetCookie("app-auth-token", user.token, { maxAge: 365 * Time.ADay })
          }
          updateApi();
          navigate("/console/", { replace: true });  
        } catch (e) {
          navigate("/console/login/");
        }
      }}
      sx={{
        height: "48px",
        backgroundColor: getHexCode("White"),
        maxWidth: "200px",
        minWidth: "150px",
        flex: 1,
        "& .MuiSelect-icon": {
          top: "50%",
          transform: "translateY(-50%)",
        },
        "& .MuiSelect-iconOpen": {
          transform: "translateY(-50%) rotate(180deg)",
        },
      }}
      renderValue={() => 
        <Stack direction="row" sx={{ alignItems: "center", minWidth: "100px" }} gap={1} pr={1}>
          <img src={onlineState ?  Icons.GreenIcon_1 : Icons.GrayIcon}/>
          <Typography sx={{ overflow: "hidden", whiteSpace: "nowrap", textOverflow: "ellipsis", width: "150px" }}>{currentUser?.name ?? "　"}</Typography>
        </Stack>  
      }
      displayEmpty
      native={false}
      MenuProps={{
        sx: {
          '& .MuiMenuItem-root':{ width: "300px" }
        }
      }}
      IconComponent={ArrowIcon}
    >
      {currentUsers && idb && currentUsers.map((user) => {
        return (
          <MenuItem key={user.id} value={user.id} sx={{ display: "flex", alignItems: "center", justifyContent: "center", py: 0 }}>
            <Stack direction="row" flex={1} alignItems="center" justifyContent="space-between">
              <Typography fontSize={16}>{user.name}</Typography>
              <Button onClick={async (e) => {
                e.stopPropagation();
                idb.loginUserDataApi.Delete(user.id).then(updateCurrentUsers);
                if (user.id === currentUser?.id) {
                  DeleteCookie("app-auth-token");
                }
              }}>
                <Typography fontSize={16} color={getHexCode("SubBlue")} sx={{ textDecoration: "underline" }}>ログアウト</Typography>
              </Button>
            </Stack>
          </MenuItem>
        )
      })}
      <MenuItem key="new" value={"new"} sx={{ height: "32px", display: "flex", alignItems: "center", justifyContent: "center" }}>
        <img width={12} height={12} src={Icons.AddIcon} />
      </MenuItem>
    </Select>
  );
}